import React, { useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { DataGrid } from "@material-ui/data-grid";
import { NavBar } from "../../widgets";
import Toolbar from "@material-ui/core/Toolbar";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import ButtonGroup from "@material-ui/core/ButtonGroup";
import TextField from "@material-ui/core/TextField";
import Backdrop from "@material-ui/core/Backdrop";
import CircularProgress from "@material-ui/core/CircularProgress";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Paper from "@material-ui/core/Paper";
import GetAppIcon from "@material-ui/icons/GetApp";
import ControlledSnackbar from "../../widgets/ControlledSnackbar";
import { Column } from "../../widgets";
import { fetchUsageReport } from "../../data/usage-store";

const useStyles = makeStyles((theme) => ({
  controls: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: theme.spacing(2),
    margin: theme.spacing(2),
  },
  dateField: {
    width: 170,
  },
  grid: {
    flex: "1 1 auto",
    margin: theme.spacing(2),
  },
  title: {
    flexGrow: 1,
  },
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: "#fff",
  },
  detailPanel: {
    margin: theme.spacing(2),
  },
  detailTable: {
    marginTop: theme.spacing(1),
  },
  exportRow: {
    display: "flex",
    justifyContent: "flex-end",
    margin: theme.spacing(0, 2),
  },
}));

function getMonthRange(monthsAgo) {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth() - monthsAgo, 1);
  const end =
    monthsAgo === 0
      ? now
      : new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59);

  return {
    start: formatDate(start),
    end: formatDate(end),
  };
}

function getLastNMonthsRange(months) {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth() - months, 1);

  return {
    start: formatDate(start),
    end: formatDate(now),
  };
}

function formatDate(date) {
  return date.toISOString().split("T")[0];
}

function formatDateDisplay(dateStr) {
  const date = new Date(dateStr + "T00:00:00");
  return date.toLocaleDateString("default", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

const columns = [
  {
    field: "name",
    headerName: "Organization",
    width: 250,
  },
  {
    field: "totalInspections",
    headerName: "Inspections",
    width: 150,
    type: "number",
  },
  {
    field: "totalReadings",
    headerName: "Readings",
    width: 150,
    type: "number",
  },
  {
    field: "userCount",
    headerName: "Active Users",
    width: 150,
    type: "number",
  },
];

function mapReportToRows(organizations) {
  return organizations.map((org) => ({
    id: org.id,
    name: org.name,
    totalInspections: org.totalInspections,
    totalReadings: org.totalReadings,
    userCount: org.users.length,
  }));
}

function generateCsv(reportData, startDate, endDate) {
  const headers = [
    "Organization",
    "User",
    "Inspections",
    "Readings",
    "Start Date",
    "End Date",
  ];

  const rows = [];

  for (const org of reportData.organizations) {
    if (org.users.length === 0) {
      rows.push([org.name, "", org.totalInspections, org.totalReadings, startDate, endDate]);
    } else {
      for (const user of org.users) {
        rows.push([org.name, user.name, user.inspections, user.readings, startDate, endDate]);
      }
    }
  }

  const csvContent = [
    headers.join(","),
    ...rows.map((row) =>
      row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(",")
    ),
  ].join("\n");

  return csvContent;
}

function downloadCsv(csvContent, filename) {
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export default function UsageReport() {
  const classes = useStyles();

  const thisMonth = getMonthRange(0);
  const [startDate, setStartDate] = useState(thisMonth.start);
  const [endDate, setEndDate] = useState(thisMonth.end);
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [snackbarState, setSnackbarState] = useState({});

  const handleQuickSelect = (preset) => {
    let range;

    switch (preset) {
      case "thisMonth":
        range = getMonthRange(0);
        break;
      case "lastMonth":
        range = getMonthRange(1);
        break;
      case "last3":
        range = getLastNMonthsRange(3);
        break;
      case "last6":
        range = getLastNMonthsRange(6);
        break;
      case "lastYear":
        range = getLastNMonthsRange(12);
        break;
      default:
        return;
    }

    setStartDate(range.start);
    setEndDate(range.end);
  };

  const handleGenerate = async () => {
    setLoading(true);
    setReportData(null);
    setSelectedOrg(null);

    try {
      // Add one day to end date so the range is inclusive of the full end day
      const endPlusOne = new Date(endDate + "T00:00:00");
      endPlusOne.setDate(endPlusOne.getDate() + 1);

      const data = await fetchUsageReport(
        startDate + "T00:00:00.000Z",
        endPlusOne.toISOString()
      );
      setReportData(data);
    } catch (error) {
      setSnackbarState({
        open: true,
        message: "Failed to generate usage report.",
        severity: "error",
        error: error,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRowClick = (params) => {
    const orgId = params.row.id;
    const org = reportData?.organizations.find((o) => o.id === orgId);
    setSelectedOrg(org);
  };

  const handleExport = () => {
    if (!reportData) return;

    const csv = generateCsv(reportData, startDate, endDate);
    const filename = `usage-report_${startDate}_${endDate}.csv`;
    downloadCsv(csv, filename);
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbarState({ ...snackbarState, open: false });
  };

  const rows = reportData ? mapReportToRows(reportData.organizations) : [];

  return (
    <React.Fragment>
      <NavBar>
        <Toolbar>
          <Typography variant="h6" className={classes.title}>
            Usage Report
          </Typography>
        </Toolbar>
      </NavBar>

      <div className={classes.controls}>
        <ButtonGroup size="small" variant="outlined">
          <Button onClick={() => handleQuickSelect("thisMonth")}>
            This Month
          </Button>
          <Button onClick={() => handleQuickSelect("lastMonth")}>
            Last Month
          </Button>
          <Button onClick={() => handleQuickSelect("last3")}>
            Last 3 Months
          </Button>
          <Button onClick={() => handleQuickSelect("last6")}>
            Last 6 Months
          </Button>
          <Button onClick={() => handleQuickSelect("lastYear")}>
            Last Year
          </Button>
        </ButtonGroup>

        <TextField
          className={classes.dateField}
          label="Start Date"
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          size="small"
          variant="outlined"
        />
        <TextField
          className={classes.dateField}
          label="End Date"
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          size="small"
          variant="outlined"
        />

        <Button
          variant="contained"
          color="primary"
          onClick={handleGenerate}
          disabled={loading || !startDate || !endDate}
        >
          Generate Report
        </Button>
      </div>

      {reportData && rows.length > 0 && (
        <div className={classes.exportRow}>
          <Typography variant="body2" style={{ flex: 1, alignSelf: "center" }}>
            {formatDateDisplay(startDate)} &mdash; {formatDateDisplay(endDate)}
            &nbsp;&nbsp;&bull;&nbsp;&nbsp;
            {reportData.organizations.length} organization
            {reportData.organizations.length !== 1 ? "s" : ""}
            &nbsp;&nbsp;&bull;&nbsp;&nbsp;
            {reportData.organizations.reduce(
              (sum, o) => sum + o.totalReadings,
              0
            )}{" "}
            total readings
          </Typography>
          <Button
            variant="outlined"
            startIcon={<GetAppIcon />}
            onClick={handleExport}
          >
            Download CSV
          </Button>
        </div>
      )}

      {reportData && rows.length === 0 && !loading && (
        <Column spacing={2} horizontalAlignment="centerHorizontally">
          <img src="images/NoDocuments@2x.png" alt="" />
          <Typography>No readings found for this time period</Typography>
        </Column>
      )}

      {(loading || rows.length > 0) && (
        <DataGrid
          className={classes.grid}
          rows={rows}
          columns={columns}
          loading={loading}
          onRowClick={handleRowClick}
          autoHeight={rows.length < 20}
        />
      )}

      {selectedOrg && selectedOrg.users.length > 0 && (
        <Paper className={classes.detailPanel} variant="outlined">
          <Typography variant="subtitle1" style={{ padding: 16 }}>
            {selectedOrg.name} &mdash; User Breakdown
          </Typography>
          <Table size="small" className={classes.detailTable}>
            <TableHead>
              <TableRow>
                <TableCell>User</TableCell>
                <TableCell align="right">Inspections</TableCell>
                <TableCell align="right">Readings</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {selectedOrg.users.map((user) => (
                <TableRow key={user.id}>
                  <TableCell>{user.name}</TableCell>
                  <TableCell align="right">{user.inspections}</TableCell>
                  <TableCell align="right">{user.readings}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      {selectedOrg && selectedOrg.users.length === 0 && (
        <Paper className={classes.detailPanel} variant="outlined">
          <Typography
            variant="body2"
            style={{ padding: 16 }}
            color="textSecondary"
          >
            {selectedOrg.name} has no user activity for this time period.
          </Typography>
        </Paper>
      )}

      <Backdrop className={classes.backdrop} open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <ControlledSnackbar state={snackbarState} onClose={handleSnackbarClose} />
    </React.Fragment>
  );
}
